/**
 * Boston Bowel Prep Scale (BBPS) — `BACKLOG.md` M5. Three colon segments (right colon, transverse
 * colon, left colon), each scored 0-3 by the endoscopist after washing/suctioning, total 0-9.
 *
 * **UNREVIEWED — the "adequate prep" threshold, not the scale itself.** The 0-3 per-segment scale and
 * its 0-9 total are a fixed, unambiguous scoring instrument (Lai et al., the scale's own defining
 * publication) — recording and summing a clinician's three segment scores carries no numeric-threshold
 * risk. What *is* flagged: `isAdequateBowelPrep` below applies a commonly-cited "adequate prep" rule
 * (total ≥ 6 **and** no individual segment scored below 2) that this module has not verified against a
 * primary source this session — the same PubMed-fetch-blocked constraint `BACKLOG.md` M5's own entry
 * names for the USMSTF 2020 surveillance-interval table (`surveillanceIntervals.ts`). Per
 * `claude-kit/rules/invariants.md`'s "never hard-code an unverified clinical threshold as if it were
 * validated fact": the engine is built for real, but this specific threshold must not be treated as
 * clinically validated, wired into a real coverage/quality decision, or displayed to a clinician as
 * settled guidance until confirmed against a primary source (e.g. the ASGE/ACG quality-indicator
 * consensus statement) and that citation is recorded here.
 *
 * `withdrawalTimeMinutes`/`isAdequateWithdrawalTime`/`MINIMUM_WITHDRAWAL_MINUTES` are the one
 * exception to the "unverified" framing above: confirmed 2026-08-24 against the current ASGE-ACG
 * Quality Indicators for Colonoscopy (2024 update, Am J Gastroenterol / Gastrointest Endosc, Sept
 * 2024) — the threshold was raised from the commonly-cited ≥6 minutes to ≥8 minutes, and this module
 * carries the current, cited figure rather than the superseded one.
 */

export const BOWEL_PREP_SEGMENT_SCORES = [0, 1, 2, 3] as const;
export type BowelPrepSegmentScore = (typeof BOWEL_PREP_SEGMENT_SCORES)[number];

export function isBowelPrepSegmentScore(value: number): value is BowelPrepSegmentScore {
  return (BOWEL_PREP_SEGMENT_SCORES as readonly number[]).includes(value);
}

export interface BostonBowelPrepScore {
  readonly rightColon: BowelPrepSegmentScore;
  readonly transverseColon: BowelPrepSegmentScore;
  readonly leftColon: BowelPrepSegmentScore;
}

/** The 0-9 total. Pure arithmetic — no threshold judgment, so nothing here is UNREVIEWED. */
export function totalBbpsScore(score: BostonBowelPrepScore): number {
  return score.rightColon + score.transverseColon + score.leftColon;
}

/**
 * UNREVIEWED — see this module's header. Total ≥ 6 with every segment ≥ 2. Do not present this as a
 * validated clinical determination until the threshold is confirmed against a primary source.
 */
export function isAdequateBowelPrep(score: BostonBowelPrepScore): boolean {
  return (
    totalBbpsScore(score) >= 6 &&
    score.rightColon >= 2 &&
    score.transverseColon >= 2 &&
    score.leftColon >= 2
  );
}

/**
 * Confirmed 2026-08-24 against the current ASGE-ACG Quality Indicators for Colonoscopy (2024
 * update, Am J Gastroenterol / Gastrointest Endosc, Sept 2024): the withdrawal-time performance
 * threshold was raised from ≥6 to ≥8 minutes. The prior ≥6 value carried over here was the older,
 * superseded figure.
 */
export const MINIMUM_WITHDRAWAL_MINUTES = 8;

export function isAdequateWithdrawalTime(withdrawalTimeMinutes: number): boolean {
  return withdrawalTimeMinutes >= MINIMUM_WITHDRAWAL_MINUTES;
}
